import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../context/AuthContext';
import { generateQuery, generateQueryStream, generateQueryStreamEventSource, getLLMStatus } from '../../api/llm';

const QueryGeneratorRealtime = ({ onQueryGenerated, isGeneratorAvailable = true }) => {
  const [prompt, setPrompt] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isConnected, setIsConnected] = useState(true); // Always connected with POST requests
  const [streamingText, setStreamingText] = useState('');
  const [streamingStatus, setStreamingStatus] = useState('');
  const { isAuthenticated, user } = useAuth();
  
  // Refs to track streaming state
  const isStreamingRef = useRef(false);
  const accumulatedTextRef = useRef('');
  
  // Add a debounce timer ref to control UI updates
  const debounceTimerRef = useRef(null);

  // Initialize component when it mounts (no socket needed anymore)
  useEffect(() => {
    // Clean up any pending timers on unmount
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
        debounceTimerRef.current = null;
      }
    };
  }, []);
  
  // Function to simulate typing at a controlled rate
  const simulateTyping = useRef((text, startPos = 0, onComplete = null) => {
    // If no typing simulation is in progress, start a new one
    if (debounceTimerRef.current === null) {
      // Clone the current text to avoid race conditions
      const currentText = text || '';
      let currentPos = startPos || 0;
      
      const typeNextChunk = () => {
        // Make sure we haven't gone past the end of the text
        if (currentPos <= currentText.length) {
          // Update the visible text with the next portion
          setStreamingText(currentText.substring(0, currentPos));
          
          // Schedule the next update
          // Slower typing speed - approximately 10 tokens per second 
          // (about 50 characters per second with 5 chars per token)
          const charsPerStep = 3; // characters to add per step
          const delay = 60; // ms between steps (60ms × 3 chars = ~50 chars/sec)
          
          currentPos += charsPerStep;
          
          debounceTimerRef.current = setTimeout(typeNextChunk, delay);
        } else {
          // Typing complete
          debounceTimerRef.current = null;
          if (onComplete) onComplete();
        }
      };
      
      // Start typing
      typeNextChunk();
    }
  }).current;
  
  // Handle streaming updates from POST endpoint
  const handleStreamingUpdate = (data) => {
    console.log('Stream update received:', data);
    
    if (data.type === 'status') {
      setStreamingStatus(data.content);
    } else if (data.type === 'chunk') {
      // Real-time token received - same logic as ref.js
      console.log('DEBUG - Received chunk:', data.content);
      const accumulatedText = data.accumulated;
      accumulatedTextRef.current = accumulatedText;
      console.log('DEBUG - Accumulated content:', accumulatedText);
      
      // Update streaming text immediately (no typing simulation needed)
      setStreamingText(accumulatedText);
      setStreamingStatus(''); // Clear status when we start getting content
      
    } else if (data.type === 'complete') {
      // Final query received
      let finalQuery = data.content;
      
      // Clear any in-progress typing
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
        debounceTimerRef.current = null;
      }
      
      // Set final text and call parent callback
      setStreamingText(finalQuery);
      
      if (finalQuery && onQueryGenerated) {
        onQueryGenerated(finalQuery);
      }
      
      // Reset state
      setIsLoading(false);
      setPrompt('');
      setStreamingStatus('');
      isStreamingRef.current = false;
      
      // Don't clear streaming text immediately to let user see the result
      setTimeout(() => {
        if (!isLoading) { // Only clear if still not loading
          setStreamingText('');
          accumulatedTextRef.current = '';
        }
      }, 1000);
    }
  };
  
  // Handle streaming errors
  const handleStreamingError = (error) => {
    console.error('Stream error:', error);
    
    // Clear any in-progress typing
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }
    
    // Reset state
    isStreamingRef.current = false;
    accumulatedTextRef.current = '';
    setStreamingText('');
    setStreamingStatus('');
    
    setError(error || 'Query generation failed');
    setIsLoading(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!prompt.trim()) {
      setError('Please enter a prompt for the query.');
      return;
    }
    
    setIsLoading(true);
    setError(null);
    setStreamingText(''); // Clear any previous streaming text
    setStreamingStatus(''); // Clear any previous status
    accumulatedTextRef.current = ''; // Reset accumulated text
    isStreamingRef.current = false; // Reset streaming state
    
    try {
      console.log('Using EventSource streaming endpoint for query generation');
      
      // Try EventSource first for better streaming support
      const query = await generateQueryStreamEventSource(
        prompt,
        256, // max_tokens
        handleStreamingUpdate,
        handleStreamingError
      );
      
      // If streaming completed successfully but no final query was received through the stream,
      // we might need to handle it here as fallback
      if (query && !isStreamingRef.current) {
        console.log('Received final query outside of streaming:', query);
        if (onQueryGenerated) {
          onQueryGenerated(query);
        }
        setPrompt('');
        setIsLoading(false);
        setStreamingStatus('');
      }
      
    } catch (streamError) {
      console.warn('Streaming POST failed, falling back to regular API:', streamError);
      
      // Fall back to regular REST API
      try {
        const query = await generateQuery(prompt);
        
        if (onQueryGenerated) {
          onQueryGenerated(query);
        }
        
        // Clear state after successful generation
        setPrompt('');
        setIsLoading(false);
        setStreamingStatus('');
      } catch (apiError) {
        console.error('API fallback error:', apiError);
        setError(apiError.message || 'Failed to generate query');
        setIsLoading(false);
        setStreamingStatus('');
      }
    }
  };
  
  if (!isGeneratorAvailable) {
    return null;
  }
  
  return (
    <div className="p-4 bg-ui-light-card dark:bg-ui-dark-card rounded-lg shadow-md">
      <h3 className="text-lg font-medium text-ui-light-text-primary dark:text-ui-dark-text-primary mb-2 flex items-center">
        AI Query Generator
        {isConnected && (
          <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-status-success-light-bg dark:bg-status-success-dark-bg text-status-success-light-text dark:text-status-success-dark-text">
            Live
          </span>
        )}
      </h3>
      <p className="text-sm text-ui-light-text-tertiary dark:text-ui-dark-text-tertiary mb-4">
        Describe what you want to query in natural language, and our AI will generate the SQL for you.
      </p>
      
      {error && (
        <div className="p-3 mb-4 text-sm text-status-error-light-text dark:text-status-error-dark-text bg-status-error-light-bg dark:bg-status-error-dark-bg rounded-md border border-status-error-light-text/20 dark:border-status-error-dark-text/20">
          {error}
        </div>
      )}
      
      <form onSubmit={handleSubmit}>
        <div className="mb-3">
          <textarea
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            className="w-full px-3 py-2 text-ui-light-text-primary dark:text-ui-dark-text-primary bg-ui-light-card dark:bg-ui-dark-card border border-ui-light-border dark:border-ui-dark-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 dark:focus:ring-offset-ui-dark-background disabled:opacity-50"
            rows="3"
            placeholder="Example: Show me all galaxies with redshift greater than 0.5 ordered by magnitude"
            disabled={isLoading}
          ></textarea>
        </div>
        
        {/* Show streaming status when loading */}
        {isLoading && streamingStatus && (
          <div className="mb-4 mt-2">
            <div className="text-sm font-medium text-ui-light-text-primary dark:text-ui-dark-text-primary mb-1 flex items-center">
              <span className="inline-block h-2 w-2 rounded-full bg-primary-500 animate-pulse mr-2"></span>
              {streamingStatus}
            </div>
          </div>
        )}

        {/* Show streaming preview when loading */}
        {isLoading && streamingText && (
          <div className="mb-4 mt-2">
            <div className="text-sm font-medium text-ui-light-text-primary dark:text-ui-dark-text-primary mb-1 flex items-center">
              <span className="inline-block h-2 w-2 rounded-full bg-primary-500 animate-pulse mr-2"></span>
              AI is generating in real-time...
            </div>
            <div className="p-3 border border-ui-light-border dark:border-ui-dark-border rounded-md bg-ui-light-background/50 dark:bg-ui-dark-background/50 font-mono text-sm text-ui-light-text-secondary dark:text-ui-dark-text-secondary whitespace-pre-wrap">
              {streamingText}
              <span className="inline-block animate-pulse">|</span>
            </div>
          </div>
        )}
        
        <div className="flex justify-end">
          <button
            type="submit"
            className="px-4 py-2 text-sm font-medium text-white dark:text-ui-dark-text-primary bg-primary-600 dark:bg-primary-700 rounded-md hover:bg-primary-700 dark:hover:bg-primary-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 dark:focus:ring-offset-ui-dark-background transition-colors disabled:opacity-50"
            disabled={isLoading || !prompt.trim()}
          >
            {isLoading ? (
              <span className="flex items-center">
                <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white dark:text-ui-dark-text-primary" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Generating...
              </span>
            ) : (
              'Generate Query'
            )}
          </button>
        </div>
      </form>
      
      <div className="mt-3 text-xs text-ui-light-text-muted dark:text-ui-dark-text-muted">
        <p>Examples:</p>
        <ul className="list-disc pl-5 space-y-1">
          <li 
            className="cursor-pointer text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 transition-colors" 
            onClick={() => setPrompt("Find all galaxies within 1 degree of RA=150.5, Dec=-2.3 with redshift greater than 0.5")}
          >
            Find all galaxies within 1 degree of RA=150.5, Dec=-2.3 with redshift greater than 0.5
          </li>
          <li 
            className="cursor-pointer text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 transition-colors" 
            onClick={() => setPrompt("Make a cone search to match GAIA and SPLUS catalogs within 0.1 arcsec")}
          >
            Make a cone search to match GAIA and SPLUS catalogs within 0.1 arcsec
          </li>
          <li 
            className="cursor-pointer text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 transition-colors" 
            onClick={() => setPrompt("Show me stars with magnitude less than 20 ordered by distance from Earth")}
          >
            Show me stars with magnitude less than 20 ordered by distance from Earth
          </li>
        </ul>
      </div>
    </div>
  );
};

export default QueryGeneratorRealtime;